import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Mirrors the Figma `Button` component set. Figma's `Style` property is exposed as
 * `variant` here because `style` is taken by the DOM, and its `State` property is
 * driven by the native `disabled` attribute rather than a prop.
 *
 * Exported so anchors and router links can be styled as buttons without nesting
 * an interactive element inside another.
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap transition-colors select-none ' +
    'disabled:cursor-not-allowed disabled:bg-bg-elevated disabled:text-text-disabled ' +
    'disabled:border-transparent disabled:shadow-none',
  {
    variants: {
      variant: {
        primary: 'bg-action-primary text-text-on-action hover:bg-action-primary-hover',
        secondary:
          'border border-border-default bg-bg-surface text-text-primary hover:bg-bg-elevated',
        ghost: 'bg-transparent text-text-primary hover:bg-bg-elevated',
        danger: 'bg-status-danger text-text-on-action hover:brightness-110',
      },
      size: {
        sm: 'min-h-10 gap-1 rounded-sm px-[18px] py-3 text-label-s',
        md: 'min-h-12 gap-2 rounded-md px-5 py-3.5 text-label-m font-bold',
        lg: 'min-h-[58px] gap-2 rounded-md px-6 py-[18px] text-body-l font-bold',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  },
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;

/** Icon sizes the Figma component uses at each button size. */
export const BUTTON_ICON_SIZE = { sm: 16, md: 20, lg: 24 } as const;
